/**
 * Markdown -> HTML formatter (adapted from your TypeScript implementation)
 * This version emits plain semantic tags (h1,h2,h3,p,ul,ol,li,code,pre,blockquote,a)
 * without Tailwind utility classes so the chat container's CSS can style them
 * correctly for dark mode.
 */
export function formatMarkdownResponse(text) {
  if (!text) return "";

  let formatted = text;

  // Convert headers first
  formatted = formatted.replace(/^\s*###\s?(.*)$/gim, "<h3>$1</h3>");
  formatted = formatted.replace(/^\s*##\s?(.*)$/gim, "<h2>$1</h2>");
  formatted = formatted.replace(/^\s*#\s?(.*)$/gim, "<h1>$1</h1>");

  // Convert bold text
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Convert inline code
  formatted = formatted.replace(/`(.*?)`/g, "<code>$1</code>");

  // Convert markdown links [text](url)
  formatted = formatted.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Auto-link plain URLs that aren't already in anchor tags
  const parts = formatted.split(/(<a\s[^>]*>.*?<\/a>)/gs);
  formatted = parts
    .map((part) => {
      if (part.startsWith("<a ")) return part;
      return part.replace(/(https?:\/\/[^\s<>\"]+)/g, (match, url) => {
        try {
          const urlObj = new URL(url);
          const hostname = urlObj.hostname.replace(/^www\./, "");
          const path = urlObj.pathname;
          let displayText = hostname;
          if (path && path.length > 20) {
            displayText = `${hostname}/...`;
          } else if (path && path !== "/") {
            displayText = `${hostname}${path.substring(0, 20)}${path.length > 20 ? "..." : ""}`;
          }
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" title="${url}">${displayText}</a>`;
        } catch (e) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" title="${url}">Visit Link</a>`;
        }
      });
    })
    .join("");

  // Process bullet lists
  const lines = formatted.split("\n");
  const result = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isRegularBullet = /^[-*] (.*)$/.test(line.trim());
    const isNestedBullet = /^\s{4,}[-*] (.*)$/.test(line);

    if (isRegularBullet || isNestedBullet) {
      const content = isNestedBullet
        ? line.replace(/^\s{4,}[-*] (.*)$/, "$1")
        : line.replace(/^[-*] (.*)$/, "$1");
      const processedContent = content.replace(
        /\*(\w+[^*/]*\w+|\w+)\*/g,
        "<em>$1</em>"
      );
      const item = `<li>${processedContent}</li>`;
      if (!inList) {
        result.push("<ul>");
        inList = true;
      }
      result.push(item);
    } else {
      if (inList) {
        result.push("</ul>");
        inList = false;
      }
      result.push(line);
    }
  }

  if (inList) result.push("</ul>");
  formatted = result.join("\n");

  // Italic
  formatted = formatted.replace(
    /\*([^*\s].*?[^*\s]|[^*\s]+)\*/g,
    "<em>$1</em>"
  );

  // Numbered lists - group consecutive numbered lines into a single <ol>
  {
    const linesNum = formatted.split("\n");
    const out = [];
    let inOl = false;
    for (let i = 0; i < linesNum.length; i++) {
      const line = linesNum[i];
      const m = line.match(/^\s*(\d+)\.\s+(.*)$/);
      if (m) {
        const content = m[2];
        if (!inOl) {
          out.push("<ol>");
          inOl = true;
        }
        out.push(`<li>${content}</li>`);
      } else {
        if (inOl) {
          out.push("</ol>");
          inOl = false;
        }
        out.push(line);
      }
    }
    if (inOl) out.push("</ol>");
    formatted = out.join("\n");
  }

  // Blockquotes
  formatted = formatted.replace(
    /^\s*>\s?(.*$)/gim,
    "<blockquote>$1</blockquote>"
  );

  // Dividers
  formatted = formatted.replace(/^\s*---\s*$/gim, "<hr/>");

  // Paragraphs
  formatted = formatted
    .split("\n\n")
    .map((paragraph) => {
      paragraph = paragraph.trim();
      if (!paragraph) return "";
      if (
        paragraph.startsWith("<h") ||
        paragraph.startsWith("<ul") ||
        paragraph.startsWith("<ol") ||
        paragraph.startsWith("<blockquote") ||
        paragraph.startsWith("<hr")
      )
        return paragraph;
      return `<p>${paragraph}</p>`;
    })
    .join("");

  // Clean up consecutive paragraphs
  formatted = formatted.replace(/<\/p>\s*<p>/g, "<br/>");

  return formatted;
}
